import React, { useEffect, useState } from "react";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import ProductionUnitApi from "../../services/ProductionUnitApi";
import PlantApi from "../../services/PlantApi";
import IApiProductionUnitResponse from "../../interfaces/IApiProductionUnitResponse";
import { useToast } from "../../context/ToastContext";

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "ListProductionUnits_filters";

const ListProductionUnits: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { t: translateMessages } = useTranslation("messages");

  const [filteredProductionUnits, setFilteredProductionUnits] = useState<IApiProductionUnitResponse[]>([]);
  const [initialData, setInitialData] = useState<IApiProductionUnitResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters state
  const [filterName, setFilterName] = useState("");
  const [filterPlantIds, setFilterPlantIds] = useState<string[]>([]);
  const [plants, setPlants] = useState<any[]>([]);

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // Pagination/Sort state (Client-side)
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    fetchProductionUnits();
    fetchPlants();
  }, []);

  useEffect(() => {
    if (initialData.length > 0) {
      if (savedFilters.name) setFilterName(savedFilters.name.value);
      if (savedFilters.plantIds) setFilterPlantIds(savedFilters.plantIds.value || []);
      applyFilters(savedFilters);
    }
  }, [initialData]);

  const fetchProductionUnits = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await ProductionUnitApi.GetAllProductUnit();
      const fetched = response.data || [];
      setInitialData(fetched);
      setFilteredProductionUnits(fetched);
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlants = async (): Promise<void> => {
    try {
      const response = await PlantApi.GetAllPlants();
      const fetched = response.data || [];
      setPlants(fetched.map((p: any) => ({
        id: p.id,
        label: p.name,
        description: p.name,
        value: p.id
      })));
    } catch (error) {
      console.error(error);
    }
  };

  const applyFilters = (filters: Record<string, any>): void => {
    let filtered = [...initialData];

    if (filters.name?.value) {
      filtered = filtered.filter((u) =>
        u.name.toLowerCase().includes(filters.name.value.toLowerCase())
      );
    }

    if (filters.plantIds?.value?.length > 0) {
      filtered = filtered.filter((u) =>
        filters.plantIds.value.includes(u.plant?.id)
      );
    }

    setFilteredProductionUnits(filtered);
    setPage(0);
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      plantIds: filterPlantIds.length > 0 ? { value: filterPlantIds } : undefined,
    };

    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    applyFilters(filters);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterPlantIds([]);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    setFilteredProductionUnits(initialData);
    setPage(0);
  };

  const deleteProductionUnit = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await ProductionUnitApi.DeleteProductUnit(id);
      displayToast("success", translateMessages("deleteSuccess"), "", false, 3000);
      fetchProductionUnits();
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (unit: IApiProductionUnitResponse): void => {
    confirmDialog({
      message: translateMessages("deleteProductionUnit"),
      header: translateGeneral("confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: translateForm("Yes"),
      rejectLabel: translateForm("No"),
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => deleteProductionUnit(unit.id),
    });
  };

  const handlePageChange = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
  };

  const handleSortChange = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const actionBodyTemplate = (rowData: IApiProductionUnitResponse): JSX.Element => (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <IconButton
        icon="visibility"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/production-units/${rowData.id}`)}
        aria-label={translateGeneral("seeDetails")}
      />
      <IconButton
        icon="edit"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/production-units/${rowData.id}/edit`)}
        aria-label={translateGeneral("edit")}
      />
      <IconButton
        icon="delete"
        variant="text"
        severity="secondary"
        onClick={() => confirmDelete(rowData)}
        aria-label={translateGeneral("delete")}
      />
    </div>
  );

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("productionUnits")}
        description="Gerencie as unidades de produção do sistema"
      >
        <Button
          label={translateGeneral("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/production-units/create")}
        />
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FilterPanel
          title="Filtros"
          onApply={handleFilter}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={translateForm("name")}
            placeholder="Pesquisar por nome"
            value={filterName}
            onChange={(val: any) => setFilterName(val)}
          />
          <Input
            type="multiselect"
            label={translateForm("plant")}
            placeholder="Selecione as plantas"
            value={filterPlantIds}
            options={plants}
            onChange={(val: any) => setFilterPlantIds(val)}
          />
        </FilterPanel>

        <DataTable
          value={filteredProductionUnits}
          paginated
          first={page}
          rows={rows}
          totalRecords={filteredProductionUnits.length}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[10, 20, 50]}
          loading={isLoading}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          onSort={handleSortChange}
          emptyMessage={translateGeneral("noRecordsFound")}
        >
          <Column field="name" header={translateForm("name")} sortable />
          <Column field="plant.name" header={translateForm("plant")} sortable />
          <Column
            header={translateForm("actions")}
            body={actionBodyTemplate}
            style={{ width: "8rem" }}
          />
        </DataTable>
      </div>
      <ConfirmDialog />
    </div>
  );
};

export default ListProductionUnits;
